import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { audioControllerHooks, AudioPlayState } from '@/manager/manager/audio-controller-manager';
import { cn } from '@/utils/cn';
import { Image } from '@/components/common/image';
import { Copy, Download, Edit3, ImageIcon, LinkIcon, RefreshCcw, Save, UserIcon } from 'lucide-react';
import { Spinner } from '@/components/ui/modal';
import { TruncatedText } from '@/components/common/truncated-text';
import { VideoIcon } from './assets/video-icon';
import { PostIcon } from './assets/post-icon';
import { PodcastIcon } from './assets/podcast-icon';
import { MomentsIcon } from './assets/moments-icon';
import { ArticleIcon } from './assets/article-icon';
import { PlayIcon } from './assets/play-icon';
import { StopIcon } from './assets/stop-icon';
import { QuoteIcon } from './assets/quote-icon';
import { GenerateIcon } from './assets/generate-icon';
import { SearchIcon } from './assets/search-icon';
import { IdeaIcon } from './assets/idea-icon';
import { Step1Icon } from './assets/step-1-icon';
import { Step2Icon } from './assets/step-2-icon';
import { Step3Icon } from './assets/step-3-icon';
import { AudioControllerBar } from '../audio-controller-bar';
import { useTranslation } from 'react-i18next';
import { useMomentsTime } from '@/hooks/use-moments-time';
import { TrashIcon, WarningIcon } from '../ui/icon';
import { IconButton } from '../common/icon-button';
import { useDownload } from '@/hooks/use-download';
import { useToast } from '@/hooks/use-toast';
import { Tooltip } from "@/components/ui/modal";
import { MoreIcon } from '../agent-dashboard/assets';
import { MoreActionItem, MoreActionPopover } from '../common/more-action-popover';
import { ConfirmModal } from '../common/confirm-modal';
import momentsBg2 from '@/assets/agent/moments-bg2.webp'
import { TextAnimateGradient } from '../common/text-animate-gradient';
import { ThinkingProcess } from './thinking-process';
import { ThinkingData } from '@/services/push-info-origin-data-record-list';
import { AgentShareModal } from '../agent-share-modal';
import { PublishIcon, ShareIcon } from '../agent-share-modal/assets';
import creationGenerating1 from '/public/app_videos/agent/creation-generating1.mp4'
import creationGeneratingCover1 from '/public/app_images/agent/creation-generating-cover1.webp'
import creationGenerating2 from '/public/app_videos/agent/creation-generating2.mp4'
import creationGeneratingCover2 from '/public/app_images/agent/creation-generating-cover2.webp'
import { Video } from '../common/video';
import { CopyClipboard } from '../common/copy-clipboard';
import { CopyIcon } from './assets/copy-icon';
import { ActorNewIcon, MaterialIcon } from '../assets/assets';
import { ImageRightIcon } from './assets/image-right-icon';
import { ImageHoverPreview } from '../common/image-hover-preview';
import ImageStack from '../common/image-stack';
import { ModalTagMove } from '../assets-material-item/modal-tag-move';
import { assetsMaterialHooks, assetsTagsHook } from '@/manager';
import { UploadModal } from '../custom-digital-human/upload-modal';
import { getModelNameByValue } from './constants/ai-creation-model';
import { AiMakeMessageRecordData } from '@/services/ai-make-source-record-list-by-type';
import { InfoCircleIcon } from './assets/info-circle-icon';
import { FallbackImage } from '../common/fallback-image';
import { buildImageOssResize } from '@/utils/build-image-oss-resize ';
import { buildVideoOssFrame } from '@/utils/build-video-oss-frame';
import { FileBgIcon } from '../agent-dashboard-order-management/assets';

const iconMap = {
  1: VideoIcon,
  2: PodcastIcon,
  3: MomentsIcon,
  4: PostIcon,
  5: ArticleIcon,
} as const;

const labelMap = {
  1: 'video',
  2: 'podcast',
  3: 'moments',
  4: 'post',
  5: 'article',
} as const;

interface Props {
  type: number;
  date: string | number | Date;
  thumbnail?: string;
  inputText?: string;
  noteName?: string;
  linkUrl?: string;
  showQuote?: boolean;
  showMoreAction?: boolean;
  itemCount?: number;
  children: React.ReactNode[] | React.ReactNode;
  deleteLoading?: boolean;
  thinkingList?: ThinkingData[];
  thinkingTitle?: string;
  startTyping?: boolean;
  showThinkingProcess?: boolean;
  isAiCreationMessage?: boolean;
  aiCreationType?: string;
  aiCreationModel?: string;
  duration?: string;
  aspectRatio?: string;
  imageSize?: string;
  inputAddress?: string;
  isFontEndFrameImage?: boolean;
  onChoose?: (id: string) => Promise<void>;
  onDelete?: () => void;
  onRegenerate?: () => void;
  onReedit?: () => void;
};

// 公共头部
export const CommonHeader: React.FC<{
  inputText: string;
  createdAt: Date | string | number;
  thumbnail?: string;
  noteName?: string;
  linkUrl?: string;
  type?: number;
  showQuote?: boolean;
  isAiCreationMessage?: boolean;
  aiCreationModel?: string;
  duration?: string;
  aspectRatio?: string;
  imageSize?: string;
  inputAddress?: string;
  isFontEndFrameImage?: boolean;
}> = ({ inputText, createdAt, thumbnail, noteName, linkUrl, type, showQuote, isAiCreationMessage, aiCreationModel, duration, aspectRatio, imageSize, inputAddress = '', isFontEndFrameImage = false }) => {
  const { formatMomentsTime } = useMomentsTime();
  const isAutoPush = !inputText && !linkUrl && !noteName && !isAiCreationMessage;

  const [isOverFlow, setIsOverFlow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const checkHeight = () => {
      if (containerRef.current) {
        const contentHeight = containerRef.current.scrollHeight;
        // 判断高度是否超过 60px
        setIsOverFlow(contentHeight > 60);
      }
    };
    // 初始执行
    checkHeight();
    // 监听窗口缩放，防止响 应式布局下宽度改变导致高度变化
    window.addEventListener('resize', checkHeight);
    return () => window.removeEventListener('resize', checkHeight);
  }, [inputText, inputAddress, thumbnail]);

  return (
    <div className='w-full flex flex-col gap-3'>
      <div className={cn("w-full text-center text-sm text-[rgba(152,162,179,1)]", isAutoPush && "mb-3")}>
        {formatMomentsTime(createdAt)}
      </div>
      {!isAutoPush && (
        <div className=" max-w-full mb-3">
          <div
            ref={containerRef}
            className="relative text-sm leading-[26px] line-clamp-2 overflow-hidden break-all text-[#1D2939]"
          >
            {showQuote && (
              <QuoteIcon className="inline-block mr-2.5 w-3 h-3 text-[rgba(16,24,40,1)] align-top flex-shrink-0" />
            )}
            {isAiCreationMessage && inputAddress && <span className="inline-block translate-y-[7px] relative z-50 mr-2">
              <ImageStack images={inputAddress?.split(',')} isFontEndFrameImage={isFontEndFrameImage} />
            </span>}
            {!isAiCreationMessage && thumbnail && (
              <span className="inline-block mr-2.5 rounded-[3px] overflow-hidden w-5 h-5 bg-[rgba(240,243,248,1)] align-middle">
                <ImageHoverPreview
                  className="w-[155px] h-[268px]"
                  url={thumbnail}
                >
                  <Image className="w-full h-full object-cover" src={thumbnail} alt="thumb" />
                </ImageHoverPreview>
              </span>
            )}
            {inputText && (
              <span className="relative align-middle text-[rgba(29,41,57,1)]">
                {inputText}
              </span>
            )}
            <span className={cn(
              'inline-block align-middle ml-[10px] bg-[#f9fafb]',
              isOverFlow && "absolute bottom-0 right-0 "
            )}>
              {isOverFlow && <span className='text-[rgba(29,41,57,1)] pl-1 mr-[10px]'>...</span>}
              {/* {isOverFlow && (
                <span className='translate-y-[5px] w-5 h-5 pl-1 inline-block bg-gradient-to-r from-transparent to-gray-200'></span>
              )} */}
              {inputText && (
                <span className="inline-flex items-center mr-[10px] align-middle translate-y-[-1px]">
                  <CopyClipboard
                    text={inputText}
                    className="size-[18px]"
                    icon={<CopyIcon className="size-4 flex-shrink-0" />}
                  />
                </span>
              )}
              {aiCreationModel && <span className='text-sm text-[#98A2B3]'>{getModelNameByValue(aiCreationModel)}</span>}
              {aspectRatio && <span className='inline-block w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />}
              {aspectRatio && <span className='text-sm text-[#98A2B3]'>{aspectRatio}</span>}
              {imageSize && <span className='inline-block w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />}
              {imageSize && <span className='text-sm text-[#98A2B3]'>{imageSize}</span>}
              {duration && <span className='inline-block w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />}
              {duration && <span className='text-sm text-[#98A2B3]'>{duration}s</span>}
            </span>
          </div>
          {noteName && (
            <Tooltip
              side='top'
              align='center'
              wrapperClassName="inline-block align-middle"
              trigger={
                <span className="inline-block max-w-[235px] mt-2 bg-white hover:bg-[rgba(234,236,240,1)] rounded-[6px] px-1.5 h-7 leading-[18px] transition-all align-middle cursor-default">
                  <span className="leading-7 block truncate text-[rgba(16,24,40,1)] text-sm font-medium">
                    {noteName}
                  </span>
                </span>
              }
              content={
                <div className="relative max-w-[384px] bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]">
                  <span className="block text-[rgba(255,255,255,1)] text-sm font-medium break-all">
                    {noteName}
                  </span>
                </div>
              }
            />
          )}
          {linkUrl && (
            <span
              className="inline-block bg-white hover:bg-[rgba(234,236,240,1)] rounded-[6px] px-2 h-7 transition-all cursor-pointer align-middle mt-2"
              onClick={() => window.open(linkUrl)}
            >
              <span className="leading-7 max-w-[235px] block truncate text-[rgba(16,24,40,1)] text-sm">
                <LinkIcon className="size-4 inline-block mr-1.5 flex-shrink-0" />
                {linkUrl}
              </span>
            </span>
          )}
        </div>
      )}
    </div >
  );
};

export const ContentGroup: React.FC<Props> = ({
  type,
  date,
  thumbnail,
  inputText,
  noteName,
  linkUrl,
  deleteLoading,
  startTyping,
  showThinkingProcess,
  thinkingList,
  thinkingTitle = 'Thinking...',
  showQuote = true,
  showMoreAction = true,
  itemCount = 1,
  isAiCreationMessage = false,
  aiCreationType,
  aiCreationModel,
  duration,
  aspectRatio,
  imageSize,
  inputAddress,
  isFontEndFrameImage,
  children,
  onDelete,
  onRegenerate,
  onReedit,
}) => {
  const { t } = useTranslation();
  const { formatMomentsTime } = useMomentsTime();
  const Icon = iconMap[type];
  const labelKey = labelMap[type];
  const typeLabel: Record<'video' | 'podcast' | 'moments' | 'post' | 'article', string> = {
    video: t('agent_creation.video'),
    podcast: t('agent_creation.podcast'),
    moments: t('agent_creation.moments'),
    post: t('agent_creation.post'),
    article: t('agent_creation.article'),
  };
  const label = typeLabel[labelKey];

  // 是否为自动推送的消息
  const isAutoPush = !inputText && !linkUrl && !noteName;

  return (
    <div className='max-w-[620px] w-full h-auto'>
      <CommonHeader
        inputText={inputText}
        createdAt={date}
        thumbnail={thumbnail}
        noteName={noteName}
        linkUrl={linkUrl}
        type={type}
        showQuote={showQuote}
        isAiCreationMessage={isAiCreationMessage}
        aiCreationModel={aiCreationModel}
        imageSize={imageSize}
        duration={duration}
        aspectRatio={aspectRatio}
        inputAddress={inputAddress}
        isFontEndFrameImage={isFontEndFrameImage}
      />
      {/* 思考过程 */}
      {showThinkingProcess && inputText && !isAiCreationMessage && (
        <ThinkingProcess
          thinkingList={thinkingList}
          startTyping={startTyping}
          title={thinkingTitle}
          isThinking={false}
          enabledExpand
        />
      )}
      {/* Ai创作的消息 */}
      {isAiCreationMessage && (
        <div className={cn(
          'rounded-[20px] max-w-[520px]',
          aiCreationType === 'image' && 'max-w-[620px]',
        )}>
          <div className='w-full h-full'>
            {children}
          </div>
          <div className='mt-3'>
            <MessageBottomActionGroup
              deleteLoading={deleteLoading}
              onReedit={onReedit}
              onRegenerate={onRegenerate}
              onDelete={onDelete}
            />
          </div>
        </div>
      )}
      {/* 其他消息 */}
      {!isAiCreationMessage && <section className='p-5 bg-white rounded-[20px] max-w-[520px]'>
        <div className='flex items-center gap-2 mb-4'>
          {Icon ? <Icon className="w-4 h-4" /> : null}
          <strong className='text-base font-medium  text-black'>{label}</strong>
          <span className='text-xs text-[rgba(102,112,133,1)]'>{formatMomentsTime(date)}</span>
          {showMoreAction && (
            <div className='ml-auto'>
              <MoreActionPopover
                className="w-40"
                side="bottom"
                tiggerIcon={
                  <MoreIcon className="size-5 text-[rgba(16,24,40,1)]" />
                }
              >
                {!isAutoPush && <>
                  <MoreActionItem
                    icon={<Edit3 className="w-4 h-4" />}
                    className='text-[rgba(16,24,40,1)] font-medium'
                    label={t("agent_creation.content.reedit")}
                    onClick={onReedit}
                  />
                  {/* <MoreActionItem
                    icon={<RefreshCcw className="w-4 h-4" />}
                    className='text-[rgba(16,24,40,1)] font-medium'
                    label={t("agent_creation.content.regenerate_again")}
                    onClick={onRegenerate}
                  /> */}
                </>
                }
                <ConfirmModal
                  trigger={
                    <MoreActionItem
                      icon={<TrashIcon className="w-4 h-4" />}
                      className='text-[rgba(16,24,40,1)] font-medium'
                      label={t("common.delete")}
                    />
                  }
                  title={t('agent_creation.content.delete_title')}
                  description={itemCount > 1 ? t('agent_creation.content.delete_desc_count', { count: itemCount }) : t('agent_creation.content.delete_desc')}
                  cancel={t('common.cancel')}
                  confirm={t('common.delete')}
                  onConfirm={onDelete}
                  loading={deleteLoading}
                />
              </MoreActionPopover>
            </div>
          )}
        </div>
        <div className="flex flex-col">
          {React.Children.toArray(children).map((child, index, arr) => (
            <React.Fragment key={index}>
              {React.cloneElement(child as React.ReactElement)}

              {arr.length > 1 && index < arr.length - 1 && (
                <div className="my-[10px] h-px bg-[rgba(233,234,235,1)]" />
              )}
            </React.Fragment>
          ))}
        </div>
      </section>}
    </div>
  );
};

/* 文章 - 列表项 */
export const ArticleListItem: React.FC<{
  image: string;
  title: string;
  showDelete?: boolean;
  deleteLoading?: boolean;
  onDelete?: () => void;
  onTap?: () => Promise<void>;
}> = ({ image, title, showDelete, deleteLoading, onDelete, onTap }) => {
  const { t } = useTranslation();
  return (
    <div onClick={onTap} className="flex gap-3 items-center cursor-pointer group">
      <Image className="w-20 h-20 object-cover rounded-[12px] border border-[#000000]/10" src={image} alt="article" />
      <TruncatedText as="p" text={title} className="flex-1 text-sm text-[#1D2939] font-normal whitespace-normal line-clamp-2" />
      {showDelete && (
        <div className='ml-auto opacity-0 group-hover:opacity-100  transition-all w-6'>
          <MoreActionPopover
            className="w-40"
            side="bottom"
            tiggerIcon={
              <MoreIcon className="size-4 text-[rgba(71,84,103,1)]" />
            }
          >
            <ConfirmModal
              trigger={
                <MoreActionItem
                  icon={<TrashIcon className="w-4 h-4" />}
                  className='text-[rgba(16,24,40,1)] font-medium'
                  label={t("common.delete")}
                />
              }
              title={t('agent_creation.content.delete_title')}
              description={t('agent_creation.content.delete_desc')}
              cancel={t('common.cancel')}
              confirm={t('common.delete')}
              onConfirm={onDelete}
              loading={deleteLoading}
            />
          </MoreActionPopover>
        </div>
      )}
    </div>
  );
};

/* 文章 - 单项 */
export const ArticleListSingleItem: React.FC<{
  image: string;
  title: string;
  onTap?: () => Promise<void>;
}> = ({ image, title, onTap }) => {
  return (
    <div onClick={onTap} className="cursor-pointer">
      <Image
        className="w-full aspect-[2/1] object-cover rounded-md"
        src={image}
        alt="article"
      />
      <TruncatedText as="p" text={title} className="mt-3 text-[#1f2937] whitespace-normal line-clamp-2" />
    </div>
  );
};

/* 朋友圈 - 列表项（多图宫格） */
type MomentTileGrid = {
  left: number;
  top: number;
  width: number;
  height: number;
  url: string;
};

const buildMomentTilesGrid = (
  urls: string[],
  n: number,
  g: number,
  w: number,
  h: number
): MomentTileGrid[] => {
  if (n === 0) return [];
  const out: MomentTileGrid[] = [];
  const tile = (
    left: number,
    top: number,
    width: number,
    height: number,
    url: string
  ) => {
    out.push({ left, top, width, height, url });
  };

  switch (n) {
    case 1: {
      tile(0, 0, w, h, urls[0]);
      break;
    }
    case 2: {
      const hTop = (h - g) / 2;
      const hBottom = h - hTop - g;
      tile(0, 0, w, hTop, urls[0]);
      tile(0, hTop + g, w, hBottom, urls[1]);
      break;
    }
    case 3: {
      const hTop = (h - g) / 2;
      const hBottom = h - hTop - g;
      const wBottomCell = (w - g) / 2;
      tile(0, 0, w, hTop, urls[0]);
      tile(0, hTop + g, wBottomCell, hBottom, urls[1]);
      tile(wBottomCell + g, hTop + g, wBottomCell, hBottom, urls[2]);
      break;
    }
    case 4: {
      const cell = (w - g) / 2;
      tile(0, 0, cell, cell, urls[0]);
      tile(cell + g, 0, cell, cell, urls[1]);
      tile(0, cell + g, cell, cell, urls[2]);
      tile(cell + g, cell + g, cell, cell, urls[3]);
      break;
    }
    case 5: {
      const bottomCell = (w - g * 2) / 3;
      const topSquare = h - g - bottomCell;
      const topRectW = w - topSquare - g;
      tile(0, 0, topSquare, topSquare, urls[0]);
      tile(topSquare + g, 0, topRectW, topSquare, urls[1]);
      for (let c = 0; c < 3; c++) {
        const left = c * (bottomCell + g);
        const top = topSquare + g;
        tile(left, top, bottomCell, bottomCell, urls[2 + c]);
      }
      break;
    }
    case 6: {
      const rightW = (w - g * 2) / 3;
      const leftTop = rightW * 2 + g;
      const bottomCell = (w - g * 2) / 3;
      tile(0, 0, leftTop, leftTop, urls[0]);
      tile(leftTop + g, 0, rightW, rightW, urls[1]);
      tile(leftTop + g, rightW + g, rightW, rightW, urls[2]);
      for (let c = 0; c < 3; c++) {
        const left = c * (bottomCell + g);
        const top = leftTop + g;
        tile(left, top, bottomCell, bottomCell, urls[3 + c]);
      }
      break;
    }
    case 7: {
      const s = (w - 2 * g) / 3;
      const rectW = w - g - s;
      tile(0, 0, rectW, s, urls[0]);
      tile(rectW + g, 0, s, s, urls[1]);
      const y2 = s + g;
      tile(0, y2, rectW, s, urls[2]);
      tile(rectW + g, y2, s, s, urls[3]);
      const y3 = y2 + s + g;
      tile(0, y3, s, s, urls[4]);
      tile(s + g, y3, s, s, urls[5]);
      tile(2 * s + 2 * g, y3, s, s, urls[6]);
      break;
    }
    case 8: {
      const s = (w - 2 * g) / 3;
      const rectW = w - g - s;
      tile(0, 0, rectW, s, urls[0]);
      tile(rectW + g, 0, s, s, urls[1]);
      const y2 = s + g;
      tile(0, y2, s, s, urls[2]);
      tile(s + g, y2, s, s, urls[3]);
      tile(2 * s + 2 * g, y2, s, s, urls[4]);
      const y3 = y2 + s + g;
      tile(0, y3, s, s, urls[5]);
      tile(s + g, y3, s, s, urls[6]);
      tile(2 * s + 2 * g, y3, s, s, urls[7]);
      break;
    }
    default: {
      const cellW = (w - g * 2) / 3;
      const cellH = (h - g * 2) / 3;
      for (let i = 0; i < 9; i++) {
        const r = Math.floor(i / 3);
        const c = i % 3;
        const left = c * (cellW + g);
        const top = r * (cellH + g);
        tile(left, top, cellW, cellH, urls[i]);
      }
    }
  }
  return out;
};

export const MomentListItem: React.FC<{
  images: string[];
  content: string;
  showDelete?: boolean;
  deleteLoading?: boolean;
  onDelete?: () => void;
  onTap?: () => Promise<void>;
}> = ({ images, content, showDelete = false, onDelete, deleteLoading, onTap }) => {
  const { t } = useTranslation();
  const urls = images.slice(0, 9); // 最多展示9张图片
  const n = urls.length; // 图片数量
  const g = 1; // 间距
  const [size, setSize] = useState(160);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tiles = buildMomentTilesGrid(urls, n, g, size, size);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(() => {
      setSize(el.clientWidth); // 保持正方形，宽即高
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="flex gap-3 items-center cursor-pointer group" onClick={onTap}>
      {!(images?.length === 1 && !images?.[0]) && images.length > 0 ? <div
        ref={containerRef}
        className="relative w-20 aspect-square rounded-[12px] overflow-hidden"
      >
        {tiles.map((t, i) => {
          const side = Math.max(t.width, t.height); // 强制 1:1 子图，覆盖填充
          const offsetLeft = (t.width - side) / 2;
          const offsetTop = (t.height - side) / 2;
          return (
            <div
              key={i}
              className="absolute overflow-hidden"
              style={{
                left: t.left,
                top: t.top,
                width: t.width,
                height: t.height,
              }}
            >
              <div
                className="absolute"
                style={{
                  left: offsetLeft,
                  top: offsetTop,
                  width: side,
                  height: side,
                }}
              >
                <Image
                  className="w-full h-full object-cover border border-[#000000]/10"
                  src={t.url}
                  alt={`img-${i}`}
                />
              </div>
            </div>
          );
        })}
      </div> : (
        <div className='relative w-20 aspect-square rounded-[12px] overflow-hidden'>
          <Image
            className="w-full h-full object-cover"
            src={momentsBg2}
          />
        </div>
      )}
      <TruncatedText as="p" text={content} className="flex-1 text-sm text-[#1D2939] font-normal whitespace-normal line-clamp-2" />
      {showDelete && (
        <div className='ml-auto opacity-0 group-hover:opacity-100 transition-all w-6'>
          <MoreActionPopover
            className="w-40"
            side="bottom"
            tiggerIcon={
              <MoreIcon className="size-4 text-[rgba(71,84,103,1)]" />
            }
          >
            <ConfirmModal
              trigger={
                <MoreActionItem
                  icon={<TrashIcon className="w-4 h-4" />}
                  className='text-[rgba(16,24,40,1)] font-medium'
                  label={t("common.delete")}
                />
              }
              title={t('agent_creation.content.delete_title')}
              description={t('agent_creation.content.delete_desc')}
              cancel={t('common.cancel')}
              confirm={t('common.delete')}
              onConfirm={onDelete}
              loading={deleteLoading}
            />
          </MoreActionPopover>
        </div>
      )}
    </div>
  );
};

/* 朋友圈 - 单项（复杂布局） */
type MomentTileLayout = {
  left: number;
  top: number;
  width: number;
  height: number;
  url: string;
  squareCrop?: boolean;
  aspect?: number;
};

const buildMomentTilesLayout = (
  urls: string[],
  n: number,
  g: number,
  w: number
): { tiles: MomentTileLayout[]; height: number } => {
  if (n === 0) return { tiles: [], height: 0 };
  const tiles: MomentTileLayout[] = [];
  const push = (
    left: number,
    top: number,
    width: number,
    height: number,
    url: string,
    squareCrop?: boolean,
    aspect?: number
  ) => tiles.push({ left, top, width, height, url, squareCrop, aspect });

  switch (n) {
    case 1: {
      const h = Math.round((w * 9) / 16);
      push(0, 0, w, h, urls[0], false, 16 / 9);
      return { tiles, height: h };
    }
    case 2: {
      const wL = (w - g) / 2;
      const wR = w - wL - g;
      const h = Math.min(wL, wR);
      push(0, 0, wL, h, urls[0], true);
      push(wL + g, 0, wR, h, urls[1], true);
      return { tiles, height: h };
    }
    case 3: {
      const wL = (w - g) * 0.6;
      const wR = w - wL - g;
      const hCell = wR;
      const h = 2 * hCell + g;
      push(0, 0, wL, h, urls[0], false, 16 / 9);
      push(wL + g, 0, wR, hCell, urls[1], true);
      push(wL + g, hCell + g, wR, hCell, urls[2], true);
      return { tiles, height: h };
    }
    case 4: {
      const wL = (w - g) * 0.6;
      const wR = w - wL - g;
      const hTop = Math.round((wR * 9) / 16);
      const wRbL = (wR - g) / 2;
      const hBottom = wRbL;
      const h = hTop + g + hBottom;
      push(0, 0, wL, h, urls[0], false, 16 / 9);
      push(wL + g, 0, wR, hTop, urls[1], false, 16 / 9);
      push(wL + g, hTop + g, wRbL, hBottom, urls[2], true);
      push(wL + g + wRbL + g, hTop + g, wR - wRbL - g, hBottom, urls[3], true);
      return { tiles, height: h };
    }
    case 5: {
      const wL = (w - g) * 0.6;
      const wR = w - wL - g;
      const cell = (wR - g) / 2;
      const h = cell * 2 + g;
      push(0, 0, wL, h, urls[0], false, 16 / 9);
      push(wL + g, 0, cell, cell, urls[1], true);
      push(wL + g + cell + g, 0, cell, cell, urls[2], true);
      push(wL + g, cell + g, cell, cell, urls[3], true);
      push(wL + g + cell + g, cell + g, cell, cell, urls[4], true);
      return { tiles, height: h };
    }
    case 6: {
      const wL = (w - g) * 0.5;
      const wR = w - wL - g;
      const cell = (wR - g) / 2;
      const h = cell * 2 + g;
      const hL = (h - g) / 2;
      push(0, 0, wL, hL, urls[0], false, 16 / 9);
      push(0, hL + g, wL, hL, urls[1], false, 16 / 9);
      push(wL + g, 0, cell, cell, urls[2], true);
      push(wL + g + cell + g, 0, cell, cell, urls[3], true);
      push(wL + g, cell + g, cell, cell, urls[4], true);
      push(wL + g + cell + g, cell + g, cell, cell, urls[5], true);
      return { tiles, height: h };
    }
    case 7: {
      const wL = (w - g) * 0.5;
      const wR = w - wL - g;
      const rightCell = (wR - g) / 2;
      const h = rightCell * 2 + g;
      const hTopL = (h - g) / 2;
      const hBottomL = h - hTopL - g;
      const wBottomCell = Math.min((wL - g) / 2, hBottomL);
      push(0, 0, wL, hTopL, urls[0], false, 16 / 9);
      push(0, hTopL + g, wBottomCell, hBottomL, urls[1], true);
      push(
        wBottomCell + g,
        hTopL + g,
        wL - wBottomCell - g,
        hBottomL,
        urls[2],
        true
      );
      push(wL + g, 0, rightCell, rightCell, urls[3], true);
      push(wL + g + rightCell + g, 0, rightCell, rightCell, urls[4], true);
      push(wL + g, rightCell + g, rightCell, rightCell, urls[5], true);
      push(
        wL + g + rightCell + g,
        rightCell + g,
        rightCell,
        rightCell,
        urls[6],
        true
      );
      return { tiles, height: h };
    }
    case 8: {
      const cell = (w - g * 3) / 4;
      const h = cell * 2 + g;
      for (let i = 0; i < 8; i++) {
        const r = Math.floor(i / 4);
        const c = i % 4;
        const left = c * (cell + g);
        const top = r * (cell + g);
        push(left, top, cell, cell, urls[i], true);
      }
      return { tiles, height: h };
    }
    default: {
      // 9 图：上 4 方，下 5 竖（3:4）
      const square = (w - g * 3) / 4;
      const rectW = (w - g * 4) / 5;
      const rectH = Math.round((rectW * 4) / 3);
      const h = square + g + rectH;

      for (let c = 0; c < 4; c++) {
        const left = c * (square + g);
        push(left, 0, square, square, urls[c], true);
      }
      for (let c = 0; c < 5; c++) {
        const left = c * (rectW + g);
        push(left, square + g, rectW, rectH, urls[4 + c], false, 3 / 4);
      }
      return { tiles, height: h };
    }
  }
};

export const MomentListSingleItem: React.FC<{
  images: string[];
  content: string;
  onDelete?: () => void;
  deleteLoading?: boolean;
  onTap?: () => Promise<void>;
}> = ({ images, content, onDelete, deleteLoading, onTap }) => {
  const urls = images.slice(0, 9); // 最多展示 9 张图片
  const n = urls.length; // 图片数量
  const g = 4; // 间距
  const [width, setWidth] = useState(320);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { tiles, height: layoutHeight } = useMemo(
    () => buildMomentTilesLayout(urls, n, g, width),
    [width, urls.join("|")]
  );
  // 固定容器高度为 2:1（宽:高），并计算纵向缩放比
  const containerHeight = Math.round(width / 2);
  const scaleY = layoutHeight > 0 ? containerHeight / layoutHeight : 1;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 320));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="cursor-pointer" onClick={onTap}>
      {!(images?.length === 1 && !images?.[0]) && images.length > 0 && <div
        className="relative overflow-hidden w-full rounded-md bg-[rgba(208,213,221,1)]"
        style={{ height: containerHeight }}
      >
        {tiles.map((t, i) => {
          const aspect = t.squareCrop ? 1 : t.aspect ?? 16 / 9;
          const wTile = t.width;
          const hTile = t.height * scaleY;

          let childW = wTile;
          let childH = childW / aspect;
          if (childH < hTile) {
            childH = hTile;
            childW = childH * aspect;
          }

          return (
            <div
              key={i}
              className="absolute overflow-hidden bg-[#f3f4f6]"
              style={{
                left: t.left,
                top: t.top * scaleY,
                width: t.width,
                height: hTile,
              }}
            >
              <div
                className="absolute"
                style={{
                  left: wTile / 2,
                  top: hTile / 2,
                  width: childW,
                  height: childH,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Image
                  className="w-full h-full object-cover"
                  src={t.url}
                  alt={`img-${i}`}
                />
              </div>
            </div>
          );
        })}
      </div>}
      <TruncatedText as="p" text={content} className="mt-3 text-[#1f2937] whitespace-normal line-clamp-2 text-sm" />
    </div>
  );
};

/* 音频 - 列表项（含播放控制） */
export const PodcastListItem: React.FC<{
  voiceThumbnail: string;
  voiceUrl: string;
  title: string;
  duration?: number;
  isActive: boolean;
  showDelete?: boolean;
  deleteLoading?: boolean;
  onDelete?: () => void;
  onTap?: () => Promise<void>;
  onTogglePlay: (showControls: boolean) => void;
}> = ({
  voiceThumbnail,
  voiceUrl,
  title,
  duration: initialDuration,
  onTap,
  isActive,
  showDelete,
  deleteLoading,
  onDelete,
  onTogglePlay,
}) => {
    const { t } = useTranslation();
    const { download } = useDownload();
    const { toast } = useToast();

    const {
      playState,
      duration,
      currentTime,
      playbackRate,
      muted,
      play,
      stop,
      volume,
      setVolume,
      setMuted,
      seekTo,
      setPlaybackRate,
      startPlaybackRateDrag,
      endPlaybackRateDrag,
      toggleMuted,
    } = audioControllerHooks.useController();

    const capsuleDurationLabel = useMemo(() => {
      const total = (duration || initialDuration || 0);
      const fmt = (t: number) => {
        const m = Math.floor(t / 60);
        const s = Math.floor(t % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
      };
      if (
        playState === AudioPlayState.IDLE ||
        playState === AudioPlayState.LOADING ||
        playState === AudioPlayState.ENDED ||
        currentTime <= 0
      ) {
        return fmt(total);
      }
      const remain = Math.max(0, total - currentTime);
      return fmt(remain);
    }, [playState, duration, currentTime, initialDuration]);

    const handleTogglePlay = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!voiceUrl) {
        return;
      }
      if (playState === AudioPlayState.PLAYING) {
        onTogglePlay(false);
        stop();
      } else {
        onTogglePlay(true);
        await play(voiceUrl);
      }
    };

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(title || '');
        toast.default({ description: t('agent_creation.agent_modal.copied') });
      } catch {
        toast.default({ description: t('agent_creation.agent_modal.copy_fail') });
      }
    };

    const handleDownload = async () => {
      try {
        await download(voiceUrl, title || 'audio');
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
      } catch {
        toast.default({ description: t('agent_creation.agent_modal.download_fail') });
      }
    };

    // 有效播放状态
    const isEffectivelyPlaying = useMemo(() => {
      return isActive &&
        playState !== AudioPlayState.IDLE &&
        playState !== AudioPlayState.ENDED;
    }, [isActive, playState]);

    return (
      <div onClick={onTap} className="group">
        {/* 顶部：左封面 + 右文案 + 播放时间胶囊 */}
        <div className="flex items-center gap-3">
          <Image
            className="w-20 h-20 object-cover rounded-[12px] shrink-0 border border-[#000000]/10"
            src={voiceThumbnail}
            alt="voice-thumbnail"
          />
          <div className="flex-1">
            <div className='h-12 mb-1 flex items-center'>
              <TruncatedText as="p" text={title} className="text-[#1D2939] whitespace-normal line-clamp-2 text-sm" />
            </div>
            <div className='flex justify-between items-center mt-auto'>
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-2 px-3 h-7 rounded-full',
                  isEffectivelyPlaying ? 'bg-[rgba(16,24,40,1)] text-white' : 'bg-[rgba(234,236,240,1)] text-[rgba(28,36,51,1)]'
                )}
                aria-label={isEffectivelyPlaying ? t('content.pause') : t('content.play')}
                onClick={handleTogglePlay}
              >
                {isEffectivelyPlaying ? (
                  <StopIcon className="w-5 h-5 text-white" />
                ) : (
                  <PlayIcon className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">{capsuleDurationLabel}</span>
              </button>
              {!isActive && (
                <div className='opacity-0 group-hover:opacity-100 transition-all flex gap-4'>
                  <IconButton
                    icon={<Copy className="w-5 h-5" />}
                    onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                  />
                  <IconButton
                    icon={<Download className="w-5 h-5" />}
                    onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                  />
                  <AgentShareModal
                    showXiaoyuzhou
                    showXiMalaya
                    trigger={
                      <IconButton
                        icon={<ShareIcon className="w-5 h-5" />}
                      />
                    }
                  />
                  {showDelete && (
                    <ConfirmModal
                      trigger={
                        <IconButton
                          icon={<TrashIcon className="w-5 h-5" />}
                        />
                      }
                      title={t('agent_creation.content.delete_title')}
                      description={t('agent_creation.content.delete_desc')}
                      cancel={t('common.cancel')}
                      confirm={t('common.delete')}
                      onConfirm={onDelete}
                      loading={deleteLoading}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 底部：播放控制条 */}
        {isEffectivelyPlaying && (
          <AudioControllerBar
            audioUrl={voiceUrl}
            copyContent={title}
            showShare
            duration={duration}
            currentTime={currentTime}
            playState={playState}
            playbackRate={playbackRate}
            muted={muted}
            volume={volume}
            seekTo={seekTo}
            setPlaybackRate={setPlaybackRate}
            startPlaybackRateDrag={startPlaybackRateDrag}
            endPlaybackRateDrag={endPlaybackRateDrag}
            toggleMuted={toggleMuted}
            setVolume={setVolume}
            setMuted={setMuted}
          />
        )}
      </div>
    );
  };

/* 图文 - 列表项（叠放三图） */
export const PostListItem: React.FC<{
  images: string[];
  title: string;
  showDelete?: boolean;
  deleteLoading?: boolean;
  onDelete?: () => void;
  onTap?: () => Promise<void>;
}> = ({ images, title, showDelete, deleteLoading, onDelete, onTap }) => {
  const { t } = useTranslation();
  return (
    <div onClick={onTap} className="flex gap-3 items-center cursor-pointer group">
      <div className="relative w-20 h-20 shrink-0">
        <Image
          className="absolute w-20 h-20 object-cover border border-[#000000]/10 rounded-md transform transform-origin-center"
          src={images[0]}
        />
      </div>
      <TruncatedText as="p" text={title} className="flex-1 text-sm text-[#1D2939] font-normal whitespace-normal line-clamp-2" />
      {showDelete && (
        <div className='ml-auto opacity-0 group-hover:opacity-100  transition-all w-6'>
          <MoreActionPopover
            className="w-40"
            side="bottom"
            tiggerIcon={
              <MoreIcon className="size-4 text-[rgba(71,84,103,1)]" />
            }
          >
            <ConfirmModal
              trigger={
                <MoreActionItem
                  icon={<TrashIcon className="w-4 h-4" />}
                  className='text-[rgba(16,24,40,1)] font-medium'
                  label={t("common.delete")}
                />
              }
              title={t('agent_creation.content.delete_title')}
              description={t('agent_creation.content.delete_desc')}
              cancel={t('common.cancel')}
              confirm={t('common.delete')}
              onConfirm={onDelete}
              loading={deleteLoading}
            />
          </MoreActionPopover>
        </div>
      )}
    </div>
  );
};

/* 图文 - 单项（封图 + 数量角标） */
export const PostListSingleItem: React.FC<{
  images: string[];
  title: string;
  onTap?: () => Promise<void>;
}> = ({ images, title, onTap }) => {
  return (
    <div onClick={onTap} className="cursor-pointer">
      <div className="relative">
        <Image
          className="w-full aspect-[2/1] object-contain rounded-md"
          src={images[0]}
          alt="post"
        />
        <div className="absolute top-2 right-2 z-10 px-1.5 min-w-[20px] h-5 rounded-full bg-[rgba(17,24,39,0.6)] text-white text-xs font-semibold flex items-center justify-center shadow-[0px_1.2px_2.4px_rgba(16,24,40,0.08)]">
          {images.length}
        </div>
      </div>
      <TruncatedText as="p" text={title} className="mt-3 text-[#1f2937] whitespace-normal line-clamp-2" />
    </div>
  );
};

/* 视频 - 列表项（含时长角标） */
export const VideoListItem: React.FC<{
  videoThumbnail: string;
  videoUrl: string;
  content: string;
  deleteLoading?: boolean;
  duration?: number; // 可选：秒数
  onDelete?: () => Promise<void>;
  onTap?: () => Promise<void>;
}> = ({ videoThumbnail, videoUrl, content, onDelete, deleteLoading, duration, onTap }) => {
  const { t } = useTranslation();
  const durationText = useMemo(() => {
    const fmt = (sec: number) => {
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60).toString().padStart(2, '0');
      const s = Math.floor(sec % 60).toString().padStart(2, '0');
      return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
    };
    return typeof duration === 'number' ? fmt(duration) : null;
  }, [duration]);

  return (
    <div onClick={onTap} className="flex gap-4 items-center cursor-pointer group">
      <div className="relative w-20 h-20 shrink-0">
        <FallbackImage
          className='w-full h-full object-cover rounded-[12px] border border-[#000000]/10'
          primarySrc={buildImageOssResize(videoThumbnail, { w: 300, h: 500 })}
          fallbackSrc={videoThumbnail || ''}
          alt={'thumbnail'}
        />
        {/* <Image
          className="w-full h-full object-cover rounded-[12px] border border-[#000000]/10"
          src={videoThumbnail}
          alt="thumbnail"
        /> */}
        {durationText && (
          <div className="absolute z-10 left-[4px] bottom-[4px] px-[4px] py-[2px] rounded-[5px] bg-[rgba(0,0,0,0.5)] text-white text-xxs font-medium">
            {durationText}
          </div>
        )}
      </div>
      <div className="flex-1">
        <TruncatedText as="p" text={content} className="text-[14px] leading-[1.4] text-[#1D2939] whitespace-normal line-clamp-2 tracking-[0.8px]" />
      </div>
      <div className='ml-auto opacity-0 group-hover:opacity-100  transition-all w-6'>
        <MoreActionPopover
          className="w-40"
          side="bottom"
          tiggerIcon={
            <MoreIcon className="size-4 text-[rgba(71,84,103,1)]" />
          }
        >
          <ConfirmModal
            trigger={
              <MoreActionItem
                icon={<TrashIcon className="w-4 h-4" />}
                className='text-[rgba(16,24,40,1)] font-medium'
                label={t("common.delete")}
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </MoreActionPopover>
      </div>
    </div>
  );
};

/* 视频 - 单项（16:9 角标时长） */
export const VideoListSingleItem: React.FC<{
  videoThumbnail: string;
  videoUrl: string;
  content: string;
  deleteLoading?: boolean;
  duration?: number; // 可选：传入秒数直接显示
  onDelete?: () => void;
  onTap?: () => Promise<void>;
}> = ({ videoThumbnail, videoUrl, content, deleteLoading, duration, onDelete, onTap }) => {
  const durationText = useMemo(() => {
    const fmt = (sec: number) => {
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60).toString().padStart(2, '0');
      const s = Math.floor(sec % 60).toString().padStart(2, '0');
      return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
    };
    return typeof duration === 'number' ? fmt(duration) : null;
  }, [duration]);



  return (
    <div onClick={onTap} className="cursor-pointer space-y-3">
      <div className="relative w-full aspect-[16/9] rounded-[16px] overflow-hidden bg-[rgba(0,0,0,1)]">
        <FallbackImage
          className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-auto'
          primarySrc={buildImageOssResize(videoThumbnail, { w: 300, h: 500 })}
          fallbackSrc={videoThumbnail || ''}
          alt={'thumbnail'}
        />
        {/* <Image
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-auto"
          src={videoThumbnail}
          alt="thumbnail"
        /> */}
        {durationText && (
          <div className="absolute left-2 bottom-2 z-10 px-2 py-1 rounded-[4px] bg-[rgba(0,0,0,0.5)] text-white text-xs font-medium">
            {durationText}
          </div>
        )}
      </div>
      <TruncatedText as="p" text={content} className="text-sm leading-[1.4] text-[#1D2939] whitespace-normal line-clamp-2 tracking-[0.8px]" />
    </div>
  );
};

// ai创作 - 视频生成
export const AiCreationVideoItem: React.FC<{
  videoThumbnail: string;
  deleteLoading?: boolean;
  records?: AiMakeMessageRecordData[];
  onDelete?: () => Promise<void>;
  onDownload?: (videoUrl: string) => Promise<void>;
  onTap?: () => Promise<void>;
  onSaveToMaterial?: () => void;
  onGenerateActor?: () => void;
  onUseReference?: () => void;
}> = ({ videoThumbnail, records, deleteLoading, onDelete, onDownload, onTap, onSaveToMaterial, onGenerateActor, onUseReference }) => {
  const { t } = useTranslation();
  const { download } = useDownload();
  const { toast } = useToast();

  const duration = records?.[0]?.videoDuration;
  const videoUrl = records?.[0]?.s3Url || records?.[0]?.url;
  const hasS3Url = records?.[0]?.s3Url ? true : false;
  // const videoUrl = '';

  const durationText = useMemo(() => {
    const fmt = (sec: number) => {
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60).toString().padStart(2, '0');
      const s = Math.floor(sec % 60).toString().padStart(2, '0');
      return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
    };
    return typeof duration === 'number' ? fmt(duration) : null;
  }, [duration]);

  // 下载
  const handleDownload = async () => {
    try {
      if (onDownload) {
        await Promise.resolve(onDownload(videoUrl));
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
        return;
      }
      await download(videoUrl, "video");
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };

  return (
    <div
      className="space-y-3"
      onClick={() => {
        if (videoUrl && onTap) {
          onTap();
        }
      }}
    >
      <div className={cn(
        "group relative w-full aspect-[16/9] rounded-[16px] overflow-hidden bg-[rgba(0,0,0,1)]",
        !videoUrl && "bg-[rgba(235,236,237,1)]",
        videoUrl && "cursor-pointer"
      )}>
        {videoUrl ? (
          <Video
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-auto"
            src={videoUrl}
            preload={hasS3Url ? 'none' : 'metadata'}
            poster={hasS3Url ? buildVideoOssFrame(videoUrl, { w: 0, h: 950 }) : ''}
          />
        ) : (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-auto flex flex-col items-center justify-center" >
            <InfoCircleIcon className="size-5 text-[rgba(180,187,192,1)] mb-2" />
            <span className="text-[rgba(180,187,192,1)] text-xs pb-1">{t('ai_creation.video_generate_fail')}</span>
            <span className="text-[rgba(180,187,192,1)] text-xs">{t('ai_creation.point_refunded')}</span>
          </div>
        )}
        {videoUrl && durationText && (
          <div className="absolute left-2 bottom-2 z-10 px-2 py-1 rounded-[4px] bg-[rgba(0,0,0,0.5)] text-white text-xs font-medium">
            {durationText}
          </div>
        )}
        {/* 下载、分享、删除 */}
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          {videoUrl && <IconButton
            className='size-[22px] hover:bg-[rgba(255,255,255,0.2)]'
            icon={<Download className="size-4 text-[rgba(255,255,255,1)]" />}
            onClick={(e) => { e.stopPropagation(); handleDownload(); }}
          />}
          {videoUrl && <AgentShareModal
            showDouyin
            douyinOptions={{
              videoUrls: [videoUrl],
            }}
            showTiktok
            tiktokOptions={{
              videoUrls: [videoUrl],
            }}
            trigger={
              <IconButton
                className='size-[22px] hover:bg-[rgba(255,255,255,0.2)]'
                icon={<PublishIcon className="size-4 text-[rgba(255,255,255,1)]" />}
              />
            }
          />}
          <MoreActionPopover
            className="w-40"
            side="bottom"
            tiggerClassName='size-[22px] hover:bg-[rgba(255,255,255,0.2)]'
            tiggerIcon={
              <MoreIcon className="size-4 text-[rgba(255,255,255,1)]" />
            }
          >
            <ConfirmModal
              trigger={
                <MoreActionItem
                  icon={<TrashIcon className="w-4 h-4" />}
                  className='text-[rgba(16,24,40,1)] font-medium'
                  label={t("common.delete")}
                />
              }
              title={t("ai_creation.delete_normal_title")}
              description={t("ai_creation.delete_normal_desc")}
              cancel={t('common.cancel')}
              confirm={t('common.delete')}
              onConfirm={onDelete}
              loading={deleteLoading}
            />
          </MoreActionPopover>
        </div>
        {/* 存入素材库、生成数字人 */}
        {videoUrl && <div className="absolute bottom-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          <SaveToMaterialButton
            className="hover:bg-[rgba(255,255,255,0.2)]"
            iconClassName="text-[rgba(255,255,255,1)]"
            url={videoUrl}
          />
          <GenerateActorButton
            className="hover:bg-[rgba(255,255,255,0.2)]"
            iconClassName="text-[rgba(255,255,255,1)]"
            onClick={onGenerateActor}
            fileUrl={videoUrl}
            type='video'
          />
        </div>}
      </div>
    </div>
  );
};

// ai创作 - 图片生成
export const AiCreationImageItem: React.FC<{
  imageUrls: string[];
  records?: AiMakeMessageRecordData[];
  deleteLoading?: boolean;
  aspectRatio?: string;
  onDelete?: (id: number) => void;
  onDownload?: (imageUrl: string) => Promise<void>;
  onTap?: (index: number) => Promise<void>;
  onSaveToMaterial?: () => void;
  onGenerateActor?: () => void;
  onUseReferenceImage?: (url: string) => void;
}> = ({
  imageUrls,
  deleteLoading,
  records,
  aspectRatio = '9:16',
  onDelete,
  onTap,
  onDownload,
  onGenerateActor,
  onUseReferenceImage
}) => {
    const { t } = useTranslation();
    const { download } = useDownload();
    const { toast } = useToast();

    // 判断是否为宽屏比例（16:9, 21:9）
    const isWideScreen = useMemo(() => {
      if (!aspectRatio) return false;
      const ratio = aspectRatio.replace(':', '/');
      return ['16/9', '21/9'].includes(ratio);
    }, [aspectRatio]);

    // 每行显示数量（宽图 2 列，其余 4 列）
    const perRow = useMemo(() => {
      return isWideScreen ? 2 : 4;
    }, [isWideScreen]);

    // 下载
    const handleDownload = async (imageUrl: string) => {
      try {
        if (onDownload) {
          await Promise.resolve(onDownload(imageUrl));
          toast.default({ description: t('agent_creation.agent_modal.download_complete') });
          return;
        }
        await download(imageUrl, "image");
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
      } catch {
        toast.default({ description: t('agent_creation.agent_modal.download_fail') });
      }
    };

    return (
      <div
        className="relative w-full"
        style={{
          maxWidth: '620px'
        }}
      >
        <div
          className="grid gap-0.5"
          style={{
            gridTemplateColumns: `repeat(${perRow}, 1fr)`
          }}
        >
          {records?.map((item, index) => {
            const url = item.s3Url || item.url;

            return (
              <div
                key={item.id}
                className="relative group min-w-0 overflow-hidden rounded-[8px]"
              >
                {url ? (
                  <Image
                    src={url}
                    className="w-full rounded-[8px] border border-[rgba(0,0,0,0.1)] object-cover cursor-pointer hover:scale-105 transition-all"
                    style={{
                      aspectRatio: aspectRatio
                        ? aspectRatio.replace(':', '/')
                        : '9/16'
                    }}
                    onClick={() => onTap?.(index)}
                    alt={`Generated image ${index + 1}`}
                  />
                ) : (
                  <div
                    className="w-full bg-[rgba(235,236,237,1)] rounded-[8px] border border-[rgba(0,0,0,0.1)] flex flex-col items-center justify-center"
                    style={{
                      aspectRatio: aspectRatio
                        ? aspectRatio.replace(':', '/')
                        : '9/16'
                    }}
                  >
                    <InfoCircleIcon className="size-5 text-[rgba(180,187,192,1)] mb-2" />
                    <span className="text-[rgba(180,187,192,1)] text-xs pb-1">
                      {t('ai_creation.image_generate_fail')}
                    </span>
                    <span className="text-[rgba(180,187,192,1)] text-xs">
                      {t('ai_creation.point_refunded')}
                    </span>
                  </div>
                )}

                {/* 顶部操作按钮 */}
                <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all p-1 bg-[rgba(0,0,0,0.4)] rounded-[8px]">
                  {url && <IconButton
                    className="size-[22px] hover:bg-[rgba(255,255,255,0.2)]"
                    icon={<Download className="size-4 text-white" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(url);
                    }}
                  />}

                  {url && <AgentShareModal
                    showDouyin
                    douyinOptions={{ imageUrls: [url] }}
                    trigger={
                      <IconButton
                        className="size-[22px] hover:bg-[rgba(255,255,255,0.2)]"
                        icon={<PublishIcon className="size-4 text-white" />}
                      />
                    }
                  />}

                  <MoreActionPopover
                    className="w-40"
                    side="bottom"
                    tiggerClassName="size-[22px] hover:bg-[rgba(255,255,255,0.2)]"
                    tiggerIcon={<MoreIcon className="size-4 text-white" />}
                  >
                    <ConfirmModal
                      trigger={
                        <MoreActionItem
                          icon={<TrashIcon className="w-4 h-4" />}
                          className="text-[rgba(16,24,40,1)] font-medium"
                          label={t("common.delete")}
                        />
                      }
                      title={t("ai_creation.delete_normal_title")}
                      description={t("ai_creation.delete_normal_desc")}
                      cancel={t('common.cancel')}
                      confirm={t('common.delete')}
                      onConfirm={async () => await onDelete?.(item.id)}
                      loading={deleteLoading}
                    />
                  </MoreActionPopover>
                </div>

                {/* 底部操作按钮 */}
                {url && (
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all p-1 bg-[rgba(0,0,0,0.4)] rounded-[8px]">
                    <SaveToMaterialButton
                      url={url}
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      iconClassName="text-white"
                    />
                    <GenerateActorButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      iconClassName="text-white"
                      onClick={onGenerateActor}
                      fileUrl={url}
                      type="image"
                    />
                    <UseReferenceButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      iconClassName="text-white"
                      onClick={() => onUseReferenceImage?.(url)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

export const FailedMessageListItem: React.FC<{
  failedType?: 'failed' | 'cancel' | 'timeout';
  createdAt: Date | string | number;
  thumbnail?: string;
  inputText: string;
  noteName?: string;
  inputAddress?: string;
  linkUrl?: string;
  type?: number;
  isAiCreationMessage?: boolean;
  deleteLoading?: boolean;
  thinkingList?: ThinkingData[];
  thinkingTitle?: string;
  duration?: string;
  aspectRatio?: string;
  imageSize?: string;
  aiCreationModel?: string;
  isFontEndFrameImage?: boolean;
  onEdit?: () => void;
  onRegenerate?: () => void;
  onDelete?: () => void;
}> = ({ failedType = 'failed', createdAt, thumbnail, inputText, noteName, inputAddress, linkUrl, type, isAiCreationMessage, deleteLoading, thinkingList, thinkingTitle, duration, aspectRatio, imageSize, aiCreationModel, isFontEndFrameImage, onEdit, onRegenerate, onDelete }) => {
  const { t } = useTranslation();
  const [openDelete, setOpenDelete] = useState(false);

  const date = typeof createdAt === 'string' || typeof createdAt === 'number' ? new Date(createdAt) : createdAt;

  const typeMap = {
    1: t('agent_creation.video'),
    2: t('agent_creation.podcast'),
    3: t('agent_creation.moments'),
    4: t('agent_creation.post'),
    5: t('agent_creation.article'),
  }

  return (
    <div className="flex flex-col  w-full max-w-[620px]">
      <CommonHeader
        inputText={inputText}
        createdAt={date}
        thumbnail={thumbnail}
        noteName={noteName}
        linkUrl={linkUrl}
        type={type}
        showQuote
        inputAddress={inputAddress}
        isAiCreationMessage={isAiCreationMessage}
        duration={duration}
        aspectRatio={aspectRatio}
        imageSize={imageSize}
        aiCreationModel={aiCreationModel}
        isFontEndFrameImage={isFontEndFrameImage}
      />
      <ThinkingProcess
        title={thinkingTitle || t('agent_creation.content.generate_failed', { type: typeMap[type] })}
        thinkingList={thinkingList}
        enabledExpand={!isAiCreationMessage}
        titleClassName={failedType === 'failed' ? 'text-[rgba(224,36,36,1)]' : ''}
        className='pb-0'
      />
      <div className="flex items-center gap-2 mt-1">
        <Tooltip
          side='top'
          align='center'
          trigger={
            <IconButton
              className='size-7'
              icon={<Edit3 className="w-5 h-5 text-[rgba(71,84,103,1)] hover:text-[rgba(16,24,40,1)]" />}
              onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            />
          }
          content={
            <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
              <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                {t("agent_creation.content.edit")}
              </span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
            </div>
          }
        />
        <Tooltip
          side='top'
          align='center'
          trigger={
            <IconButton
              className='size-7'
              icon={<RefreshCcw className="w-5 h-5 text-[rgba(71,84,103,1)] hover:text-[rgba(16,24,40,1)]" />}
              onClick={(e) => { e.stopPropagation(); onRegenerate?.(); }}
            />
          }
          content={
            <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
              <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                {t("agent_creation.content.regenerate")}
              </span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
            </div>
          }
        />
        <Tooltip
          side='top'
          align='center'
          trigger={
            <IconButton
              className='size-7'
              icon={<TrashIcon className="w-5 h-5 text-[rgba(71,84,103,1)] hover:text-[rgba(16,24,40,1)]" />}
              onClick={(e) => { e.stopPropagation(); setOpenDelete(true); }}
            />
          }
          content={
            <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
              <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                {t("agent_creation.content.delete")}
              </span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
            </div>
          }
        />
        <ConfirmModal
          open={openDelete}
          onOpenChange={setOpenDelete}
          title={t('agent_creation.content.delete_title')}
          description={t('agent_creation.content.delete_desc')}
          cancel={t('common.cancel')}
          confirm={t('common.delete')}
          onConfirm={onDelete}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

// 智能体创作的生成中
export const GeneratingMessageListItem: React.FC<{
  createdAt: Date | string | number;
  thinkingCreateTime?: number;
  thumbnail?: string;
  inputText: string;
  noteName?: string;
  linkUrl?: string;
  type: number;
  thinkingList: ThinkingData[];
  isFontEndFrameImage?: boolean;
  thinkingTitle?: string;
  isThinking: boolean;
  isQueued: boolean;
  isProcessing: boolean;
  showCancel?: boolean;
  onCancel?: () => void;
}> = ({ createdAt, thinkingCreateTime, thumbnail, inputText, noteName, linkUrl, type, thinkingList, isFontEndFrameImage, thinkingTitle, isThinking, isQueued, isProcessing, showCancel, onCancel }) => {
  const date = typeof createdAt === 'string' || typeof createdAt === 'number' ? new Date(createdAt) : createdAt;

  return (
    <div className="flex flex-col w-full max-w-[620px]">
      {/* 思考中,思考过程 */}
      {(isThinking || isQueued) && (
        <>
          <CommonHeader
            inputText={inputText}
            createdAt={date}
            linkUrl={linkUrl}
            thumbnail={thumbnail}
            noteName={noteName}
            type={type}
            showQuote={!!inputText}
            isFontEndFrameImage={isFontEndFrameImage}
          />
          <ThinkingProcess
            title={thinkingTitle}
            thinkingList={thinkingList}
            startTyping={isThinking}
            enabledExpand={isThinking}
            // expand={isThinking}
            isThinking={isThinking || isQueued}
            showCancel={showCancel}
            onCancel={onCancel}
          />
        </>
      )}
      {/* 生成中 */}
      {isProcessing && (
        <GeneratingCard
          showThinkingProcess
          inputText={inputText}
          createdAt={date}
          thinkingCreateTime={thinkingCreateTime}
          thumbnail={thumbnail}
          noteName={noteName}
          linkUrl={linkUrl}
          type={type}
          thinkingList={thinkingList}
        />
      )}
    </div>
  );
};

// AI创作的生成中
export const GeneratingAiCreationMessageListItem: React.FC<{
  createdAt: Date | string | number;
  inputText: string;
  type: number;
  inputAddress?: string;
  model?: string;
  duration?: string;
  aspectRatio?: string;
  imageSize?: string;
  isFontEndFrameImage?: boolean;
}> = ({
  createdAt,
  inputText,
  type,
  inputAddress = '',
  model,
  duration,
  aspectRatio,
  imageSize,
  isFontEndFrameImage,
}) => {
    const { t } = useTranslation();
    const date = typeof createdAt === 'string' || typeof createdAt === 'number' 
      ? new Date(typeof createdAt === 'number' && createdAt < 10000000000 ? createdAt * 1000 : createdAt) 
      : createdAt;

    const progressPercent = () => {
      const elapsed = Math.floor((Date.now() - date.getTime()) / 1000);
      const start = 8;  // 8s
      const end = 180;  // 3 min
      if (elapsed <= start) return 0;
      if (elapsed >= end) return 99;
      const ratio = (elapsed - start) / (end - start);
      return Math.floor(ratio * 99);
    };

    return (
      <div className="flex flex-col w-full max-w-[620px]">
        <CommonHeader
          inputText={inputText}
          createdAt={date}
          type={type}
          showQuote
          inputAddress={inputAddress}
          aiCreationModel={model}
          duration={duration}
          aspectRatio={aspectRatio}
          imageSize={imageSize}
          isAiCreationMessage
          isFontEndFrameImage={isFontEndFrameImage}
        />
        <div className="relative w-[520px] h-[293px]  rounded-[16px] overflow-hidden">
          <Video
            className="absolute inset-0 w-[520px] h-full object-cover"
            src={creationGenerating1}
            poster={creationGeneratingCover1}
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="text-white text-xs font-medium absolute top-[10px] left-[10px] px-2 py-1 bg-[rgba(0,0,0,0.09)] rounded-[6px] select-none">Generating {progressPercent()}%</div>
        </div>
      </div>
    )
  }

const GeneratingPreparationCard: React.FC<{ step: number }> = ({ step }) => {
  const { t } = useTranslation();
  const clamp = (n: number) => Math.max(0, Math.min(2, n | 0));
  const idx = clamp(step);
  const CurrentIcon = [GenerateIcon, SearchIcon, IdeaIcon][idx];
  const currentLabel = [
    t('agent_creation.generate.current_label1'),
    t('agent_creation.generate.current_label2'),
    t('agent_creation.generate.current_label3')
  ][idx];
  const steps = [
    { Icon: Step1Icon, label: t('agent_creation.generate.step1_label'), activeLabel: t('agent_creation.generate.step1_active') },
    { Icon: Step2Icon, label: t('agent_creation.generate.step2_label'), activeLabel: t('agent_creation.generate.step2_active') },
    { Icon: Step3Icon, label: t('agent_creation.generate.step3_label'), activeLabel: t('agent_creation.generate.step3_active') },
  ];

  return (
    <div>
      <div className="flex gap-1 flex-col w-full p-5 rounded-[20px] bg-white">
        <div className="flex gap-4 items-center">
          <div className="relative w-8 h-8">
            <Spinner className="absolute inset-0 w-8 h-8" strokeWidth={4} />
            <CurrentIcon className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-[rgba(16,24,40,1)]" />
          </div>
          <TruncatedText as="p" text={currentLabel} className="flex-1 text-base font-medium text-[rgba(16,24,40,1)] whitespace-normal" />
        </div>
        {steps.map((s, i) => {
          const actived = step >= i;
          const activeing = step === i;
          const Icon = s.Icon;
          return (
            <div key={i} className="flex gap-4 items-center">
              <div className="relative w-8 h-8">
                <Icon className={cn("absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2", actived ? "w-5 h-5 text-[rgba(16,24,40,1)]" : "w-5 h-5 text-[rgba(152,162,179,1)]")} />
              </div>
              <TruncatedText
                as="div"
                text={activeing ? s.activeLabel : s.label}
                className={cn(
                  "flex-1 text-sm font-regular",
                  actived ? "text-[rgba(16,24,40,1)]" : "text-[rgba(102,112,133,1)]",
                  activeing && "font-medium"
                )} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GeneratingCard: React.FC<{
  type: number;
  createdAt: Date;
  thinkingCreateTime?: number;
  inputText: string;
  thumbnail?: string;
  noteName?: string;
  showThinkingProcess?: boolean;
  linkUrl?: string;
  thinkingList?: ThinkingData[]
}> = ({ type, createdAt, thinkingCreateTime, inputText, thumbnail, noteName, showThinkingProcess, linkUrl, thinkingList = [] }) => {
  const { t } = useTranslation();

  const progressPercent = () => {
    const elapsed = Math.floor((Date.now() - thinkingCreateTime || 0) / 1000);
    const start = 15; // 15 sec
    const end = 180;  // 3 min
    if (elapsed <= start) return 0;
    if (elapsed >= end) return 99;
    const ratio = (elapsed - start) / (end - start);
    return Math.floor(ratio * 99);
  };

  const typeLabel: Record<number, string> = {
    1: t('agent_creation.video'),
    2: t('agent_creation.podcast'),
    3: t('agent_creation.moments'),
    4: t('agent_creation.post'),
    5: t('agent_creation.article'),
  };
  const label = typeLabel[type] || '';
  return (
    <ContentGroup
      type={type}
      date={createdAt}
      showMoreAction={false}
      inputText={inputText}
      thumbnail={thumbnail}
      noteName={noteName}
      linkUrl={linkUrl}
      thinkingTitle={t('agent_creation.content.thinking_process.thinking_completed')}
      showThinkingProcess={showThinkingProcess}
      thinkingList={thinkingList}
    >
      {type === 1 ? (
        <div className="relative w-full aspect-[16/9] rounded-[16px] overflow-hidden">
          <Video
            className="absolute inset-0 w-full h-full object-cover"
            src={creationGenerating1}
            poster={creationGeneratingCover1}
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <div className="text-white text-sm font-medium">{t('agent_creation.generate.generating', { label })}{progressPercent()}% ...</div>
          </div>
        </div>
      ) : (
        <div className='flex relative w-full h-20 rounded-[16px] overflow-hidden gap-3'>
          <div className='size-20 overflow-hidden rounded-[16px]'>
            <Video
              className="w-full h-full object-cover"
              src={creationGenerating2}
              poster={creationGeneratingCover2}
              autoPlay
              loop
              muted
              playsInline
            />
          </div>
          <div className='flex flex-col justify-center gap-1'>
            <TextAnimateGradient
              text={t('agent_creation.generate.generating', { label }) + progressPercent() + '%'}
            />
            {type === 2 && (
              <p className='text-[13px] text-[rgba(102,112,133,1)]'>{t('agent_creation.generate.generating_podcast')}</p>
            )}
            {type === 4 && <p className='text-[13px] text-[rgba(102,112,133,1)]'>{t('agent_creation.generate.generating_post')}</p>}
          </div>
        </div>
      )}
    </ContentGroup>
  );
};

// 存入素材库按钮
export const SaveToMaterialButton = ({
  url,
  className,
  iconClassName,
  onSuccess,
}: { url: string; className?: string; iconClassName?: string; onSuccess?: () => void }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  // 素材标签hook
  const { createTag, loading: createLoading } = assetsTagsHook.useCreateAssetsTag();
  const { batchUpload, loading: uploadLoading } = assetsTagsHook.useBatchUploadFolderTags();
  // 素材文件夹hook
  const { createMaterial, loading: createFolderLoading } = assetsMaterialHooks.useMaterialFolderCreate();

  const handleSave = async (data: { newFolderId: number; newTagIds: number[]; }[]) => {
    try {
      await batchUpload([{ url, target: data }]);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to batch upload assets material item tags:", error);
      throw error;
    }
  }

  return (
    <>
      <Tooltip
        side='top'
        align='center'
        trigger={
          <IconButton
            className={cn('size-[22px] hover:bg-[rgba(16,24,40,0.05)]', className)}
            icon={<MaterialIcon className={cn('size-4 text-[rgba(51,51,51,1)]', iconClassName)} />}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          />
        }
        content={
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
              {t("ai_creation.save_to_material")}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-[rgba(16,24,40,1)]"></div>
          </div>
        }
      />
      {open && (
        <ModalTagMove
          open={open}
          title={t("ai_creation.save_to_material")}
          moveButtonText={t("common.cancel")}
          copyButtonText={t("common.save")}
          moveButtonClassName='!px-10'
          copyButtonClassName='!px-10'
          moveButtonDisabled={false}
          createFolderLoading={createFolderLoading}
          createTagLoading={createLoading}
          saveLoading={uploadLoading}
          onOpenChange={setOpen}
          onCreateFolder={(name) => createMaterial({ name })}
          onCreateTag={(name, folderId) => createTag(name, folderId)}
          onCopy={handleSave}
        />
      )}
    </>
  )
}

// 生成数字人按钮
export const GenerateActorButton = ({
  className,
  iconClassName,
  type,
  fileUrl,
  onClick
}: {
  type: 'image' | 'video';
  className?: string;
  iconClassName?: string;
  fileUrl?: string;
  onClick?: () => void
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  return (
    <>
      <Tooltip
        side='top'
        align='center'
        trigger={
          <IconButton
            className={cn('size-[22px] hover:bg-[rgba(16,24,40,0.05)]', className)}
            icon={<ActorNewIcon className={cn('size-4 text-[rgba(51,51,51,1)]', iconClassName)} />}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          />
        }
        content={
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className={cn('text-[rgba(255,255,255,1)] text-sm font-medium')}>
              {t("ai_creation.generate_actor")}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-[rgba(16,24,40,1)]"></div>
          </div>
        }
      />
      {open && (
        <UploadModal
          open={open}
          onOpenChange={setOpen}
          uploadType={type}
          remoteUrl={fileUrl}
          onCreateSuccess={() => {
            toast.default({
              description: t("ai_creation.generate_actor_success"),
            });
          }}
        />
      )}
    </>
  )
}

// 用作参考图按钮
export const UseReferenceButton = ({
  className,
  iconClassName,
  onClick
}: { className?: string; iconClassName?: string; onClick?: () => void }) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      side='top'
      align='center'
      trigger={
        <IconButton
          className={cn('size-[22px] hover:bg-[rgba(16,24,40,0.05)]', className)}
          icon={<ImageRightIcon className={cn('size-4 text-[rgba(51,51,51,1)]', iconClassName)} />}
          onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        />
      }
      content={
        <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
          <span className={cn('text-[rgba(255,255,255,1)] text-sm font-medium')}>
            {t("ai_creation.use_for_reference_image")}
          </span>
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-[rgba(16,24,40,1)]"></div>
        </div>
      }
    />
  )
}

// 创作模块，消息底部操作按钮组
export const MessageBottomActionGroup = ({
  deleteLoading,
  onReedit,
  onRegenerate,
  onDelete,
}: {
  deleteLoading?: boolean;
  onReedit?: () => void;
  onRegenerate?: () => void;
  onDelete?: () => void;
}) => {
  const { t } = useTranslation();
  const [openDelete, setOpenDelete] = useState(false);

  return (
    <div className='flex items-center gap-[10px]'>
      <Tooltip
        side='top'
        align='center'
        trigger={
          <IconButton
            className='size-7'
            icon={<Edit3 className="w-5 h-5 text-[rgba(71,84,103,1)] hover:text-[rgba(16,24,40,1)]" />}
            onClick={(e) => { e.stopPropagation(); onReedit?.(); }}
          />
        }
        content={
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
              {t("ai_creation.reedit")}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-[rgba(16,24,40,1)]"></div>
          </div>
        }
      />
      <Tooltip
        side='top'
        align='center'
        trigger={
          <IconButton
            className='size-7'
            icon={<RefreshCcw className="w-5 h-5 text-[rgba(71,84,103,1)] hover:text-[rgba(16,24,40,1)]" />}
            onClick={(e) => { e.stopPropagation(); onRegenerate?.(); }}
          />
        }
        content={
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
              {t("ai_creation.generate_again")}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-[rgba(16,24,40,1)]"></div>
          </div>
        }
      />
      <Tooltip
        side='top'
        align='center'
        trigger={
          <IconButton
            className='size-7'
            icon={<TrashIcon className="w-5 h-5 text-[rgba(71,84,103,1)] hover:text-[rgba(16,24,40,1)]" />}
            onClick={(e) => { e.stopPropagation(); setOpenDelete(true); }}
          />
        }
        content={
          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
              {t("agent_creation.content.delete")}
            </span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-[rgba(16,24,40,1)]"></div>
          </div>
        }
      />
      <ConfirmModal
        open={openDelete}
        onOpenChange={setOpenDelete}
        title={t("ai_creation.delete_batch_title")}
        description={t("ai_creation.delete_batch_tips")}
        cancel={t('common.cancel')}
        confirm={t('common.delete')}
        onConfirm={onDelete}
        loading={deleteLoading}
      />
    </div>
  )
}
